import { alpha } from '@mui/material/styles';
/**
 * Exact height of a one-line field box under the v2 theme, per MUI `size`.
 * Every other box measurement (input padding, autocomplete padding, the rich
 * text minimum) is derived from these two numbers.
 */
export const V2_FIELD_HEIGHT = { small: 38, medium: 44 };
export const V2_DEFAULT_FIELD_SIZE = 'small';
const FIELD_LINE_HEIGHT = 20;
const FIELD_PADDING_X = 12;
const FIELD_BORDER_WIDTH = 1;
/** Anything but an explicit `medium` is the v2 default, including an input outside a `FormControl`. */
const resolveV2FieldSize = (size) => size === 'medium' ? 'medium' : V2_DEFAULT_FIELD_SIZE;
/**
 * The outlined input's border is an absolutely positioned fieldset and takes no
 * layout space, so the box is the line plus the padding on both sides.
 */
const getInputPaddingY = (size) => (V2_FIELD_HEIGHT[size] - FIELD_LINE_HEIGHT) / 2;
/** The rich-text box draws a real border on its own root, so the border comes off the padding. */
const getRichTextPaddingY = (size) => (V2_FIELD_HEIGHT[size] - FIELD_LINE_HEIGHT) / 2 - FIELD_BORDER_WIDTH;
/**
 * The single source of the v2 field box (fill, border, radius, focus ring,
 * disabled recess). `MuiOutlinedInput` and every bordered box that is not an
 * `OutlinedInput` (the rich-text editor) are built from these values, so the
 * two cannot drift apart.
 */
const getV2FieldBoxTokens = (theme) => {
    const { sys } = theme.palette.custom.OV;
    return {
        borderRadius: 8,
        fill: sys.surfaceContainer,
        color: sys.onBackground,
        fontSize: 13,
        borderColor: sys.outlineVariant,
        focusBorderColor: sys.primary,
        // Mockup keeps a 1px border on focus and adds a soft outer ring
        // instead of MUI's default 2px border swap. `sys.primary` is the
        // mockup teal (per-mode), same source the checked switch uses.
        focusRing: `0 0 0 2px ${alpha(sys.primary, 0.28)}`,
        // MUI's default disabled state barely reads against the v2 fill, so
        // recess the field: flatter container, dimmer 1px border, muted text.
        disabledFill: sys.surfaceContainerLow,
        disabledBorderOpacity: 0.5,
        disabledTextColor: sys.outline,
    };
};
export const getV2OutlinedInputRootStyles = (theme, { size, multiline, startAdornment, endAdornment }) => {
    const box = getV2FieldBoxTokens(theme);
    const paddingY = getInputPaddingY(resolveV2FieldSize(size));
    return {
        borderRadius: box.borderRadius,
        backgroundColor: box.fill,
        color: box.color,
        fontSize: box.fontSize,
        // A multiline input pads its root, not the textarea, and sizes itself by its rows.
        ...(multiline && {
            paddingTop: paddingY,
            paddingBottom: paddingY,
            paddingLeft: startAdornment ? undefined : FIELD_PADDING_X,
            paddingRight: endAdornment ? undefined : FIELD_PADDING_X,
        }),
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: box.borderColor,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: box.borderColor,
        },
        '&.Mui-focused': {
            boxShadow: box.focusRing,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: box.focusBorderColor,
            borderWidth: 1,
        },
        '&.Mui-disabled': {
            backgroundColor: box.disabledFill,
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: box.borderColor,
            borderWidth: 1,
            opacity: box.disabledBorderOpacity,
        },
        // Match the base disabled-text selector's specificity (root + input
        // both carry Mui-disabled); injected later, so this wins.
        '&.Mui-disabled .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: box.disabledTextColor,
        },
    };
};
/**
 * Longhand paddings only: a shorthand would clobber the right padding MUI
 * reserves for an adornment and the `.MuiSelect-select` arrow, and the
 * `height` pin must not reach a multiline textarea, which sizes itself.
 */
export const getV2OutlinedInputInputStyles = (theme, { size, multiline, startAdornment, endAdornment }) => {
    const paddingY = getInputPaddingY(resolveV2FieldSize(size));
    return {
        lineHeight: `${FIELD_LINE_HEIGHT}px`,
        ...(!multiline && {
            height: `${FIELD_LINE_HEIGHT}px`,
            paddingTop: paddingY,
            paddingBottom: paddingY,
            paddingLeft: startAdornment ? undefined : FIELD_PADDING_X,
            paddingRight: endAdornment ? undefined : FIELD_PADDING_X,
        }),
        '&::placeholder': {
            color: theme.palette.custom.OV.sys.onSurfaceVariant,
            opacity: 1,
        },
    };
};
/**
 * A button that opens a menu from inside a form is drawn as the field box: same height, fill,
 * border, focus ring and disabled recess as the outlined input next to it. An error border wins
 * over the focus border, as it does on an outlined input.
 */
export const getV2FieldTriggerStyles = (theme, { size, error, open }) => {
    const box = getV2FieldBoxTokens(theme);
    const borderColor = error ? theme.palette.error.main : box.borderColor;
    const activeBorderColor = error ? borderColor : box.focusBorderColor;
    return {
        width: '100%',
        height: V2_FIELD_HEIGHT[resolveV2FieldSize(size)],
        padding: `0 ${FIELD_PADDING_X}px`,
        borderRadius: box.borderRadius,
        border: `${FIELD_BORDER_WIDTH}px solid ${borderColor}`,
        backgroundColor: box.fill,
        color: box.color,
        fontSize: box.fontSize,
        fontWeight: theme.typography.fontWeightRegular,
        ...(open && { borderColor: activeBorderColor, boxShadow: box.focusRing }),
        '&:hover': { borderColor: open ? activeBorderColor : borderColor },
        '&:focus-visible': { borderColor: activeBorderColor, boxShadow: box.focusRing },
        '& [data-placeholder]': { color: theme.palette.custom.OV.sys.onSurfaceVariant },
        '&.Mui-disabled': {
            opacity: 1,
            backgroundColor: box.disabledFill,
            color: box.disabledTextColor,
            borderColor: alpha(box.borderColor, box.disabledBorderOpacity),
        },
    };
};
/**
 * A toggle group that sits in a row of fields is a field too: its buttons take the field
 * box's height and corner radius, so the row lines up.
 */
export const getV2ToggleGroupStyles = (theme, { size }) => {
    const { borderRadius, borderColor } = getV2FieldBoxTokens(theme);
    return {
        '& .MuiToggleButton-root': { height: V2_FIELD_HEIGHT[resolveV2FieldSize(size)] },
        // A selected button keeps the field's quiet border: a bright edge reads a pixel taller than
        // the unfocused field beside it. The extra class outranks the group's own selected rule.
        '& .MuiToggleButton-root.MuiButtonBase-root.Mui-selected': { borderColor },
        '& .MuiToggleButtonGroup-firstButton': {
            borderTopLeftRadius: borderRadius,
            borderBottomLeftRadius: borderRadius,
        },
        '& .MuiToggleButtonGroup-lastButton': {
            borderTopRightRadius: borderRadius,
            borderBottomRightRadius: borderRadius,
        },
    };
};
/**
 * The rich-text editor draws its border on its own root rather than on a
 * `.MuiOutlinedInput-notchedOutline` child, so the box tokens are applied to
 * the root itself. The selectors repeat the ones the component's base styles
 * use: an override only wins at equal specificity.
 */
export const getV2RichTextFieldBoxStyles = (theme, { error, disabled, size }) => {
    const box = getV2FieldBoxTokens(theme);
    const fieldSize = resolveV2FieldSize(size);
    const paddingY = getRichTextPaddingY(fieldSize);
    let borderColor = box.borderColor;
    if (error) {
        borderColor = theme.palette.error.main;
    }
    if (disabled) {
        borderColor = alpha(box.borderColor, box.disabledBorderOpacity);
    }
    return {
        boxSizing: 'border-box',
        minHeight: V2_FIELD_HEIGHT[fieldSize],
        borderRadius: box.borderRadius,
        backgroundColor: disabled ? box.disabledFill : box.fill,
        color: disabled ? box.disabledTextColor : box.color,
        borderColor,
        '&:hover': { borderColor },
        '.portal-editor': {
            padding: `${paddingY}px ${FIELD_PADDING_X - FIELD_BORDER_WIDTH}px`,
            lineHeight: `${FIELD_LINE_HEIGHT}px`,
        },
        '.portal-editor > *:first-of-type': {
            marginTop: 0,
        },
        '&:has(.ProseMirror:focus, .ProseMirror-focused)': {
            outline: 'none',
            borderColor: box.focusBorderColor,
            boxShadow: box.focusRing,
        },
    };
};
/**
 * The editor box has no notch in its border, so a label sitting on the border paints over it:
 * clear above the border line, the box fill below it, whatever surface the box sits on.
 */
export const getV2RichTextFieldLabelStyles = (theme) => {
    const { fill } = getV2FieldBoxTokens(theme);
    return {
        background: `linear-gradient(to bottom, transparent calc(50% - 1px), ${fill} calc(50% - 1px))`,
    };
};
